import type { IAction } from "@kuika/kuika-cl-model";
import { isDesignerActionEnabled } from "./designer-action-actions";

/**
 * Where an Action Results value comes from, and whether it can hold anything yet at the step being
 * edited — for the symbol picker's preview.
 *
 * An event's actions run top to bottom. A result bound in step N from an action at step N+k of the
 * same event is still empty when step N runs. That is the one ordering this helper claims; anything
 * it cannot pin down it leaves unknown (no warning), because a false alarm teaches people to ignore
 * the real one:
 *
 *  · Different events: when one runs relative to the other depends on the user, not on the design.
 *  · Results are offered once per (label, tag) — the engine de-duplicates them — so the same result
 *    may be written from several places. Only when EVERY place that writes it sits later in the
 *    edited action's own event is it "after"; only when every one sits earlier is it "before".
 *  · Nested actions (If / Else branches, For Each bodies) are ordered by the top-level step that
 *    holds them. Two actions under the same top-level step are left unknown, and anything nested
 *    in a step that runs on its own schedule (MQTT subscriptions, recurring actions) is unordered.
 */

export enum ESymbolPickerActionOrder {
  /** Every writer runs earlier in the edited action's event. */
  before = "before",
  /** Every writer runs later in the edited action's event — the value is still empty here. */
  after = "after"
}

/** One ordered list of top-level actions: an event of a component, or a flat action list. */
export interface ISymbolPickerActionSequence {
  /** The component the event belongs to ("Button1"); absent for a flat list. */
  ownerName?: string;
  /** "onClick"; for a flat list, what the list is ("Scheduled job"). */
  eventName?: string;
  actions: IAction[];
}

export interface ISymbolPickerActionResultOrigin {
  /** The producing action's label, as stored on the action. */
  actionLabel: string;
  actionTag?: string;
  ownerName?: string;
  eventName?: string;
  /** Undefined when the order cannot be told reliably. */
  order?: ESymbolPickerActionOrder;
}

interface IActionOccurrence {
  action: IAction;
  sequenceIndex: number;
  /** The top-level step that runs it; NaN when it runs on its own schedule. */
  stepIndex: number;
}

function forEachNested(action: IAction, visit: (nested: IAction, isOrdered: boolean) => void): void {
  const ordered: IAction[] = [
    ...(action.ifThenElseData?.ifActions ?? []),
    ...(action.ifThenElseData?.elseActions ?? []),
    ...(action.forEachData?.actions ?? [])
  ];
  const unordered: IAction[] = [
    ...(action.recurringActionsData?.recurringActions ?? []),
    ...(action.mqttSubscribeActionData?.mqttSubscribeActions ?? [])
  ];
  ordered.forEach((nested: IAction): void => visit(nested, true));
  unordered.forEach((nested: IAction): void => visit(nested, false));
}

function collectOccurrences(sequences: ISymbolPickerActionSequence[]): IActionOccurrence[] {
  const occurrences: IActionOccurrence[] = [];
  const seen: Set<IAction> = new Set<IAction>();
  sequences.forEach((sequence: ISymbolPickerActionSequence, sequenceIndex: number): void => {
    (sequence.actions ?? []).forEach((topLevel: IAction, stepIndex: number): void => {
      const walk = (action: IAction, step: number): void => {
        if (!action || seen.has(action) || !isDesignerActionEnabled(action)) {
          return;
        }
        seen.add(action);
        occurrences.push({ action, sequenceIndex, stepIndex: step });
        forEachNested(action, (nested: IAction, isOrdered: boolean): void => walk(nested, isOrdered ? step : NaN));
      };
      walk(topLevel, stepIndex);
    });
  });
  return occurrences;
}

function writesSameResult(a: IAction, b: IAction): boolean {
  return a.label === b.label && (a.tag || null) === (b.tag || null);
}

function resolveOrder(
  writers: IActionOccurrence[],
  edited: IActionOccurrence | undefined
): ESymbolPickerActionOrder | undefined {
  if (!edited || Number.isNaN(edited.stepIndex)) {
    return undefined;
  }
  const inEditedSequence: boolean = writers.every(
    (writer: IActionOccurrence): boolean =>
      writer.sequenceIndex === edited.sequenceIndex && !Number.isNaN(writer.stepIndex)
  );
  if (!inEditedSequence) {
    return undefined;
  }
  if (writers.every((writer: IActionOccurrence): boolean => writer.stepIndex > edited.stepIndex)) {
    return ESymbolPickerActionOrder.after;
  }
  if (writers.every((writer: IActionOccurrence): boolean => writer.stepIndex < edited.stepIndex)) {
    return ESymbolPickerActionOrder.before;
  }
  return undefined;
}

/**
 * The origin of the result the option `resultActionId` names, relative to the action being edited
 * (`editedActionId`; absent for a host that is not an action parameter). Undefined when the result's
 * action is not found in `sequences`.
 */
export function resolveSymbolPickerActionResultOrigin(
  sequences: ISymbolPickerActionSequence[],
  resultActionId: string,
  editedActionId: string | undefined
): ISymbolPickerActionResultOrigin | undefined {
  const occurrences: IActionOccurrence[] = collectOccurrences(sequences);
  const result: IActionOccurrence | undefined = occurrences.find(
    (occurrence: IActionOccurrence): boolean => occurrence.action.id === resultActionId
  );
  if (!result) {
    return undefined;
  }
  const writers: IActionOccurrence[] = occurrences.filter((occurrence: IActionOccurrence): boolean =>
    writesSameResult(occurrence.action, result.action)
  );
  const edited: IActionOccurrence | undefined = editedActionId
    ? occurrences.find((occurrence: IActionOccurrence): boolean => occurrence.action.id === editedActionId)
    : undefined;
  const sequence: ISymbolPickerActionSequence = sequences[result.sequenceIndex];
  return {
    actionLabel: result.action.label,
    actionTag: result.action.tag || undefined,
    ownerName: sequence.ownerName,
    eventName: sequence.eventName,
    order: resolveOrder(writers, edited)
  };
}
